import http from 'hub-http/clients/apiClient';
import { dataFetchingClient } from '../../utils/DataFetchingClient';
import { registerMutation, registerQuery, useMutation, useQuery } from 'academy-common-lib/utils/DataFetchingClient';
import { BASE_URI_PROGRESS } from '../../constants/ApiConstants';
import { BROCHURE_URL_BY_LANGUAGE } from '../../constants/AchievementConstants';
import { AWARD_VISIBILITIES } from '../../constants/TracksConstants';
import getLang from 'LearningCenterUI/utils/getLang';
import { useCallback, useMemo } from 'react';
import { updateCache } from '../../utils/DataFetchingClientCache';
import produce from 'immer';
import { create } from 'zustand';
import { publicQuickFetchOrGet } from '../../utils/publicUtils';
import { useGetByIdQuery } from '../../../__generated__/chirp/com/hubspot/academy/catalog/rpc/noauth/TrackPublicChirpService.hooks';
import { useCreateAchievementMutation } from '../../../__generated__/chirp/com/hubspot/learning/center/user/progress/rpc/AchievementChirpService.hooks';
import { updateAchievementVisibilityRpc } from '../../../__generated__/chirp/com/hubspot/learning/center/user/progress/rpc/AchievementChirpService';
import { rpcClient } from '../../client';
export const ACHIEVEMENTS_API = `${BASE_URI_PROGRESS}/achievements`;
export const denormalizePublicAchievementResponse = response => {
  const denormalizedAchievements = {};
  Object.keys(response.achievements).forEach(publicId => {
    const achievement = response.achievements[publicId];
    const user = response.users[achievement.userId];
    const track = response.tracks[achievement.tracks[0]];
    const lessons = response.lessons;
    denormalizedAchievements[publicId] = Object.assign({}, achievement, user, {
      tracks: [Object.assign({
        id: achievement.tracks[0].toString()
      }, track, {
        lessons: track.lessons.map(lessonId => Object.assign({
          id: lessonId.toString()
        }, lessons[lessonId]))
      })]
    });
  });
  return denormalizedAchievements;
};
export const GET_ALL_ACHIEVEMENTS = registerQuery({
  fieldName: 'allAchievements',
  args: [],
  fetcher() {
    return http.get(ACHIEVEMENTS_API).then(response => denormalizePublicAchievementResponse(response));
  }
});
export const GET_ACHIEVEMENT_BY_PUBLIC_ID = registerQuery({
  fieldName: 'achievementByPublicId',
  args: ['publicId'],
  fetcher({
    publicId
  }) {
    return publicQuickFetchOrGet(`${ACHIEVEMENTS_API}/public/${publicId}`).then(response => denormalizePublicAchievementResponse(response)[publicId]);
  }
});
export const UPDATE_ACHIEVEMENT = registerMutation({
  fieldName: 'updateAchievement',
  args: ['achievement', 'metadata', 'visibility'],
  fetcher({
    achievement,
    metadata,
    visibility
  }) {
    const visibilityType = visibility || achievement.visibilityType;
    return rpcClient.call(updateAchievementVisibilityRpc, {
      request: {
        achievementPublicId: achievement.publicId,
        visibilityType
      }
    }).then(() => {
      return produce(achievement, draft => {
        draft.metadata = Object.assign({}, draft.metadata, metadata);
        if (visibility) {
          draft.private = visibility !== AWARD_VISIBILITIES.PUBLIC;
          draft.visibilityType = visibility;
        }
      });
    });
  }
});
const updateAchievementsCache = achievement => {
  Object.keys(achievement).forEach(publicId => {
    updateCache(GET_ACHIEVEMENT_BY_PUBLIC_ID, () => achievement[publicId], {
      publicId
    });
  });
  updateCache(GET_ALL_ACHIEVEMENTS, previousAchievements => {
    Object.keys(achievement).forEach(publicId => {
      previousAchievements[publicId] = achievement[publicId];
    });
  }, {});
};
export const getPublicAchievement = publicId => {
  if (!publicId) return null;
  return dataFetchingClient.queryUnwrapped({
    query: GET_ACHIEVEMENT_BY_PUBLIC_ID,
    variables: {
      publicId
    },
    fetchPolicy: 'cache-first'
  });
};
export const usePublicAchievement = publicId => {
  const {
    data: publicAchievement,
    error: publicAchievementError,
    loading: publicAchievementLoading
  } = useQuery(GET_ACHIEVEMENT_BY_PUBLIC_ID, {
    variables: {
      publicId
    },
    skip: !publicId,
    fetchPolicy: 'cache-first'
  });
  return {
    publicAchievement,
    publicAchievementError,
    publicAchievementLoading
  };
};
export const getAllAchievements = ({
  fetchPolicy = 'cache-first'
} = {}) => {
  return dataFetchingClient.queryUnwrapped({
    query: GET_ALL_ACHIEVEMENTS,
    fetchPolicy
  }).then(achievements => {
    updateAchievementsCache(achievements);
    return achievements;
  });
};
export const useAllAchievements = () => {
  const {
    data: allAchievements,
    error: allAchievementsError,
    loading: allAchievementsLoading
  } = useQuery(GET_ALL_ACHIEVEMENTS, {
    onCompleted: data => {
      updateAchievementsCache(data.allAchievements);
    }
  });
  return {
    allAchievements,
    allAchievementsError,
    allAchievementsLoading
  };
};
export const getBrochureRoot = language => BROCHURE_URL_BY_LANGUAGE[language] || BROCHURE_URL_BY_LANGUAGE.en;
export const useAchievementByTrackId = trackId => {
  const {
    allAchievements,
    allAchievementsLoading,
    allAchievementsError
  } = useAllAchievements();
  const foundAchievement = useMemo(() => {
    return Object.values(allAchievements || {}).find(achievement => achievement.tracks[0].id === trackId);
  }, [allAchievements, trackId]);
  return {
    achievement: foundAchievement,
    achievementsLoading: allAchievementsLoading,
    achievementsError: allAchievementsError
  };
};
export const useAchievementTracks = publicId => {
  const {
    publicAchievement
  } = usePublicAchievement(publicId);
  if (!publicAchievement) return null;
  return publicAchievement.tracks;
};
export const useAchievementUser = publicId => {
  const {
    publicAchievement
  } = usePublicAchievement(publicId);
  if (!publicAchievement) return null;
  return {
    userId: publicAchievement.userId,
    avatarHash: publicAchievement.avatarHash,
    firstName: publicAchievement.firstName,
    lastName: publicAchievement.lastName
  };
};
export const useAchievementBrochureUrl = (publicId, language) => {
  const tracks = useAchievementTracks(publicId);
  if (!tracks) return null;
  return tracks[0].metadata[language || getLang()].brochureUrl;
};
export const useAchievementCertificateUrl = publicId => {
  const tracks = useAchievementTracks(publicId);
  if (!tracks) return null;
  return tracks[0].award && tracks[0].award.certificate;
};
export const useCreateAchievement = achievementData => {
  const [mutate, {
    data: createAchievementData,
    loading: createAchievementLoading,
    error: createAchievementError,
    called: createAchievementCalled
  }] = useCreateAchievementMutation({
    variables: achievementData
  });
  const createAchievement = useCallback(async options => {
    const mutationResult = await mutate(options);
    // Bypass cache to get fresh data including new achievement
    void getAllAchievements({
      fetchPolicy: 'network-only'
    });
    return mutationResult;
  }, [mutate]);
  return [createAchievement, {
    createAchievementData,
    createAchievementLoading,
    createAchievementError,
    createAchievementCalled
  }];
};
export const updateAchievement = (achievement, metadata, visibility) => {
  return dataFetchingClient.mutate({
    mutation: UPDATE_ACHIEVEMENT,
    variables: {
      achievement,
      metadata,
      visibility
    },
    update(__, result) {
      if (!result.data) return;
      const updatedAchievement = result.data.updateAchievement;
      updateAchievementsCache({
        [updatedAchievement.publicId]: updatedAchievement
      });
    }
  });
};
const useAchievementUpdating = create(set => ({
  isUpdatingAchievement: {},
  setIsUpdatingAchievement: (publicId, isUpdatingAchievement) => set(produce(state => {
    state.isUpdatingAchievement[publicId] = isUpdatingAchievement;
  }))
}));
export const useUpdateAchievement = (achievement, metadata, visibility) => {
  const [mutate, {
    data: updateAchievementData,
    loading: updateAchievementLoading,
    error: updateAchievementError,
    called: updateAchievementCalled
  }] = useMutation(UPDATE_ACHIEVEMENT, {
    variables: {
      achievement: achievement,
      metadata,
      visibility
    },
    update(__, result) {
      if (!result.data) return;
      const updatedAchievement = result.data.updateAchievement;
      updateAchievementsCache({
        [updatedAchievement.publicId]: updatedAchievement
      });
    }
  });
  const {
    isUpdatingAchievement,
    setIsUpdatingAchievement
  } = useAchievementUpdating();
  const mutateAchievement = useCallback(() => {
    if (achievement) {
      setIsUpdatingAchievement(achievement.publicId, true);
      return mutate().then(data => {
        setIsUpdatingAchievement(achievement.publicId, false);
        return data;
      });
    }
    return null;
  }, [achievement, mutate, setIsUpdatingAchievement]);
  return [mutateAchievement, {
    updateAchievementData,
    updateAchievementLoading,
    updateAchievementGlobalLoading: achievement ? isUpdatingAchievement[achievement.publicId] : false,
    updateAchievementError,
    updateAchievementCalled
  }];
};

// TODO: Change how this is handled so we are not exporting plain functions. Perhaps a hook
export function getMetadataLanguageFallback(metadata, targetLanguage) {
  return metadata[targetLanguage] ? metadata[targetLanguage] : metadata[Object.keys(metadata)[0]];
}
export const useTrackPublicData = trackId => {
  const {
    data,
    loading,
    error
  } = useGetByIdQuery({
    variables: {
      trackId: trackId
    },
    skip: !trackId
  });
  return {
    track: data === null || data === void 0 ? void 0 : data.track,
    trackLoading: loading,
    trackError: error
  };
};